/**
 * HITS OVER TIME — one line per person, counts bucketed by hour or day.
 *
 * Built against the server's /series aggregate, never by tallying fetched rows. The
 * detail endpoints are uncapped, so a busy person over 30 days is ~316k rows; a chart
 * that counted those client-side would carry the whole cost of the table it exists to
 * replace. The aggregate returns ~30 points instead.
 *
 * NOT the house LineChartComponent: that one hardcodes a dark tooltip (unreadable as a
 * light-mode surface), always renders a legend even for a single series, and takes
 * colors from the caller with no palette discipline. The rules below are the reason
 * this is its own component.
 *
 * COLOR IS ASSIGNED BY IDENTITY, IN FIXED ORDER, AND NEVER CYCLED. Slot 1 is always
 * the first person by volume, slot 2 the second, and so on. A 9th person does not get
 * a generated hue — the tail folds into a single "Other" series, because two people
 * sharing a colour is a chart that lies about who did what.
 *
 * DARK MODE IS ITS OWN SET OF STEPS, not an automatic flip of the light one: the same
 * hues re-stepped to clear 3:1 against the dark surface.
 *
 * Both palettes were validated, not eyeballed (dataviz validate_palette.js):
 *   light  all 8 inside L 0.43–0.77, chroma >= 0.1, worst adjacent CVD ΔE 9.1 (protan),
 *          normal-vision ΔE 19.6. Three slots fall below 3:1 on the light surface, so
 *          the "relief rule" applies — satisfied by the Table view this chart toggles
 *          with, which carries the same numbers as text.
 *   dark   all 8 inside L 0.48–0.67, worst adjacent CVD ΔE 8.4, all >= 3:1 contrast.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { SeriesPoint } from '../../../services/userHistoryReport.service';

/** Categorical slots, fixed order. See the header note on validation. */
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500',
  '#d55181', '#008300', '#9085e9', '#e66767'];

/** Past this many people the tail folds into one "Other" line rather than inventing
 *  hues. Matches the palette's slot count minus the slot "Other" itself occupies. */
const MAX_SERIES = 7;
const OTHER = 'Other';
const OTHER_COLOR = { light: '#52514e', dark: '#c3c2b7' };

/** Tailwind toggles `dark` on <html>; Recharts needs real values, not classes. */
const useIsDark = (): boolean => {
  const read = () => typeof document !== 'undefined'
    && document.documentElement.classList.contains('dark');
  const [dark, setDark] = useState(read);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const obs = new MutationObserver(() => setDark(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

/** A server bucket as a LOCAL timestamp.
 *
 *  Parsed by hand because `new Date(s)` is inconsistent for exactly these two shapes:
 *  a date-only "2026-10-01" is treated as UTC midnight (which west of UTC renders as
 *  Sep 30), while "2026-10-01T14:00:00" is treated as local. One of the two would
 *  silently shift by a day. */
export const parseBucket = (raw: string): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}))?/.exec(String(raw));
  if (!m) return NaN;
  return new Date(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 0).getTime();
};

const fmtBucket = (ts: number, bucket: 'day' | 'hour'): string => {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return bucket === 'hour'
    ? d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', hour12: true })
    : d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
};

export interface HitsTrendChartProps {
  points: SeriesPoint[];
  bucket: 'day' | 'hour';
  /** Window to draw, as YYYY-MM-DD. Buckets with no activity are filled with 0 across
   *  it, so the axis is the range you asked for and not merely the days with data. */
  startDate?: string | null;
  endDate?: string | null;
  loading?: boolean;
}

/** Every bucket between two timestamps, inclusive, at the given granularity.
 *  Stepped on local calendar fields rather than by adding milliseconds, so a DST
 *  change does not shift every later bucket by an hour. */
function bucketRange(from: number, to: number, bucket: 'day' | 'hour'): number[] {
  const out: number[] = [];
  const d = new Date(from);
  // Guard against a pathological range producing an unbounded array.
  for (let i = 0; d.getTime() <= to && i < 20000; i++) {
    out.push(d.getTime());
    if (bucket === 'hour') d.setHours(d.getHours() + 1);
    else d.setDate(d.getDate() + 1);
  }
  return out;
}

/**
 * Tidy rows -> one object per bucket, keyed by series name.
 *
 * EVERY SERIES GETS A VALUE IN EVERY BUCKET, and the bucket list spans the whole
 * selected window rather than only the buckets somebody was active in. This is what
 * keeps one person's line from changing shape when a second person is added: with a
 * categorical axis built from the data, selecting Lee inserts the days only Lee worked,
 * which re-spaces John's points and makes his curve look different for no reason. It
 * also removes the need to connect across gaps — a day with no activity is a real zero,
 * not an unknown to be bridged with a straight line that implies steady work.
 */
export function buildChartModel(
  points: SeriesPoint[],
  bucket: 'day' | 'hour' = 'day',
  startDate?: string | null,
  endDate?: string | null,
) {
  const totals = new Map<string, number>();
  for (const p of points) {
    totals.set(p.user_name, (totals.get(p.user_name) ?? 0) + p.hits);
  }
  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);
  const kept = ranked.slice(0, MAX_SERIES);
  const kse = new Set(kept);
  const folded = ranked.length > MAX_SERIES;
  const series = folded ? [...kept, OTHER] : kept;

  const tally = new Map<number, Record<string, number>>();
  for (const p of points) {
    const ts = parseBucket(p.bucket);
    if (isNaN(ts)) continue;
    if (!tally.has(ts)) tally.set(ts, {});
    const row = tally.get(ts)!;
    const name = kse.has(p.user_name) ? p.user_name : OTHER;
    row[name] = (row[name] ?? 0) + p.hits;
  }

  if (series.length === 0) return { rows: [], series, folded };

  // Prefer the requested window; fall back to the data's own span when either end is
  // open, so a cleared date filter still draws a continuous axis.
  const stamps = [...tally.keys()];
  const from = startDate ? parseBucket(startDate) : Math.min(...stamps);
  const to = endDate
    ? (bucket === 'hour' ? parseBucket(endDate) + 23 * 3600_000 : parseBucket(endDate))
    : Math.max(...stamps);
  if (isNaN(from) || isNaN(to) || to < from) return { rows: [], series, folded };

  const rows = bucketRange(from, to, bucket).map((ts) => {
    const hit = tally.get(ts) ?? {};
    const row: Record<string, number> = { ts };
    for (const name of series) row[name] = hit[name] ?? 0;
    return row;
  });

  return { rows, series, folded };
}

const HitsTrendChart: React.FC<HitsTrendChartProps> = ({
  points, bucket, startDate, endDate, loading,
}) => {
  const dark = useIsDark();
  const { rows, series, folded } = useMemo(
    () => buildChartModel(points, bucket, startDate, endDate),
    [points, bucket, startDate, endDate]);

  const colorFor = (name: string, i: number) =>
    (name === OTHER ? (dark ? OTHER_COLOR.dark : OTHER_COLOR.light)
      : (dark ? SERIES_DARK : SERIES_LIGHT)[i]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading chart…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        No activity in this range.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
          {/* Recessive: the grid orients, it does not compete with the marks. */}
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
          {/* A REAL time scale, not a category list. On a categorical axis every point
              is one slot wide regardless of the gap before it, so a quiet fortnight
              renders the same width as a quiet day — and the slots themselves change
              when another person is selected, moving everyone's marks. */}
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => fmtBucket(Number(v), bucket)}
            minTickGap={28}
            className="text-gray-600 dark:text-gray-400"
            tick={{ fill: 'currentColor', fontSize: 12 }}
          />
          <YAxis
            allowDecimals={false}
            width={56}
            tickFormatter={(v) => Number(v).toLocaleString()}
            className="text-gray-600 dark:text-gray-400"
            tick={{ fill: 'currentColor', fontSize: 12 }}
          />
          <Tooltip
            // Themed rather than hardcoded dark — a dark card on a light surface is
            // the house component's bug, not a style choice.
            contentStyle={{
              backgroundColor: dark ? '#1a1a19' : '#ffffff',
              border: `1px solid ${dark ? '#3f3f3b' : '#e5e7eb'}`,
              borderRadius: '0.5rem',
              color: dark ? '#ffffff' : '#0b0b0b',
              fontSize: 12,
            }}
            labelFormatter={(v) => fmtBucket(Number(v), bucket)}
            formatter={(v: any, n: any) => [Number(v).toLocaleString(), n]}
          />
          {/* One series needs no legend box: there is nothing to tell apart. */}
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {series.map((name, i) => (
            <Line
              key={name}
              // Straight segments, not `monotone`. Spline smoothing invents curvature
              // between two counts — it overshoots above the real peak and bulges above
              // zero on a quiet day, so the drawn line reports values nobody recorded.
              // These are discrete daily/hourly tallies; join them honestly.
              type="linear"
              dataKey={name}
              name={name}
              stroke={colorFor(name, i)}
              strokeWidth={2}
              // Markers only when there are few enough points to aim at; a dot per
              // hour over 30 days is 720 marks and reads as noise.
              dot={rows.length <= 40 ? { fill: colorFor(name, i), r: 4 } : false}
              activeDot={{ r: 6 }}
              // No connectNulls: zero-filling means there are no nulls, and bridging a
              // gap with a straight line would draw sustained work across days off.
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      {folded && (
        <p className="shrink-0 pt-1 text-xs text-gray-500 dark:text-gray-400">
          Showing the {MAX_SERIES} busiest people individually; everyone else is summed
          into “{OTHER}”. Select fewer people to see them separately.
        </p>
      )}
    </div>
  );
};

export default HitsTrendChart;
